/* ==========================================================================
   Lovelle — bra.js
   Everything the bra product template needs, and nothing any other page does.
   Loaded with defer, only on that template (see layout/theme.liquid).

   Deliberately kept apart from theme.js: this file owns data-bra-* attributes
   only, so the leggings bundle, the standard product page and the cart drawer
   in theme.js keep working exactly as they did. The one thing it borrows is
   window.LovelleAddToCart, so a bra lands in the same drawer as everything else.

   No user-facing string is written here. Anything the shopper can read arrives
   from Liquid via a data-* attribute or is already in the DOM, which is what
   keeps the page translatable from locales/*.json alone.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-bra-root]');
  var sizeGuideEl = document.querySelector('[data-bra-sizeguide]');
  var videoWall = document.querySelector('[data-bra-video-wall]');
  var reviewSection = document.querySelector('[data-bra-review-section]');
  var happyStrip = document.querySelector('[data-bra-happy]');
  if (!root && !sizeGuideEl && !videoWall && !reviewSection && !happyStrip) return;

  var reduced = window.matchMedia('(prefers-reduced-motion:reduce)').matches;

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ==========================================================================
     0 · COLOUR SWATCHES
     A colour name is turned into something to look at. Variant photos win when
     Shopify has one for that colour; the table is the fallback so a brand-new
     colour still gets a dot rather than a grey blank. Prints are drawn as
     repeating gradients because "Rosa leopard" is not a flat colour and a flat
     dot would misrepresent it.
     ========================================================================== */
  /* Both spellings of every colour, because on a localised storefront the
     option value arrives already translated: "Svart" is "Sort", "Grön" is
     "Grønn", "Mocka" is "Mokka". A name that matches nothing falls through to
     the neutral grey, which would turn the whole Norwegian colour picker into
     identical dots.

     Longest-first matters: "rubinröd" has to be tested before "röd", and
     "mörkebrun" before "brun". */
  var SWATCHES = [
    ['rubinröd', '#9B1B30'], ['rubinrød', '#9B1B30'], ['ruby', '#9B1B30'], ['rubiininpunainen', '#9B1B30'],
    ['vinröd', '#6E1B2E'], ['vinrød', '#6E1B2E'], ['bordeaux', '#6E1B2E'], ['wine', '#6E1B2E'], ['viininpunainen', '#6E1B2E'],
    ['mörkgrå', '#3D3D3D'], ['mørkegrå', '#3D3D3D'], ['tummanharmaa', '#3D3D3D'], ['dark grey', '#3D3D3D'],
    ['mörkbrun', '#4A3527'], ['mørkebrun', '#4A3527'], ['tummanruskea', '#4A3527'],
    ['off-white', '#F0ECE6'], ['off white', '#F0ECE6'], ['krämvit', '#F3ECE0'], ['kremhvit', '#F3ECE0'], ['kermanvalkoinen', '#F3ECE0'], ['cremefarvet', '#F3ECE0'], ['luonnonvalkoinen', '#F0ECE6'],
    ['nude', '#D9BFA9'], ['beige', '#D9C2A6'], ['sand', '#D9C2A6'], ['khaki', '#8F8B5A'], ['beessi', '#D9C2A6'], ['hiekka', '#D9C2A6'], ['sandfarvet', '#D9C2A6'],
    ['svart', '#1B1B1B'], ['black', '#1B1B1B'], ['sort', '#1B1B1B'], ['musta', '#1B1B1B'],
    ['vit', '#F2F0ED'], ['white', '#F2F0ED'], ['hvit', '#F2F0ED'], ['hvid', '#F2F0ED'], ['valkoinen', '#F2F0ED'],
    ['kaffe', '#6F4E37'], ['coffee', '#6F4E37'], ['brun', '#7A5340'], ['brown', '#7A5340'], ['kahvinruskea', '#6F4E37'], ['kahvi', '#6F4E37'], ['ruskea', '#7A5340'],
    ['rosé', '#E7BFC2'], ['rose', '#E7BFC2'], ['ruusu', '#E7BFC2'], ['pudderrosa', '#E7BFC2'], ['vaaleanpunainen', '#EFB2C4'], ['lyserød', '#EFB2C4'], ['rosa', '#EFB2C4'], ['pink', '#EFB2C4'], ['pinkki', '#EFB2C4'],
    ['röd', '#B8332B'], ['rod', '#B8332B'], ['rød', '#B8332B'], ['red', '#B8332B'], ['punainen', '#B8332B'],
    ['grå', '#9A9A9A'], ['gra', '#9A9A9A'], ['grey', '#9A9A9A'], ['gray', '#9A9A9A'], ['harmaa', '#9A9A9A'],
    ['laivastonsininen', '#1B2A4A'], ['tummansininen', '#1B2A4A'], ['mørkeblå', '#1B2A4A'], ['marinblå', '#1B2A4A'], ['marineblå', '#1B2A4A'], ['navy', '#1B2A4A'],
    ['blå', '#2A6FDB'], ['bla', '#2A6FDB'], ['blue', '#2A6FDB'], ['sininen', '#2A6FDB'],
    ['grön', '#4E7C5A'], ['gron', '#4E7C5A'], ['grønn', '#4E7C5A'], ['gronn', '#4E7C5A'], ['green', '#4E7C5A'], ['vihreä', '#4E7C5A'], ['grøn', '#4E7C5A'],
    ['oliv', '#6B6B3A'], ['olive', '#6B6B3A'], ['oliivi', '#6B6B3A'],
    ['lila', '#7A3FA0'], ['lilla', '#7A3FA0'], ['purple', '#7A3FA0'], ['liila', '#7A3FA0'], ['violetti', '#7A3FA0'], ['luumu', '#7A3FA0'], ['blomme', '#7A3FA0'], ['champagne', '#E6D5B8'], ['samppanja', '#E6D5B8'],
    ['mocka', '#8A6A55'], ['mokka', '#8A6A55'], ['mocha', '#8A6A55'], ['taupe', '#A78E7B'], ['keltainen', '#E8C33A'], ['gul', '#E8C33A'], ['oranssi', '#E2552B'], ['turkoosi', '#1AA7A0'], ['laventeli', '#B9A6D4'], ['lavendel', '#B9A6D4'], ['lavender', '#B9A6D4'], ['koralli', '#F0734E'], ['koral', '#F0734E'], ['korall', '#F0734E'], ['coral', '#F0734E'], ['turkis', '#1AA7A0'], ['turkos', '#1AA7A0'], ['teal', '#1AA7A0']
  ];

  function baseColour(name) {
    var n = (name || '').toLowerCase();
    for (var i = 0; i < SWATCHES.length; i++) {
      if (n.indexOf(SWATCHES[i][0]) > -1) return SWATCHES[i][1];
    }
    return '#CFC7BE';
  }

  function swatchStyle(name) {
    var n = (name || '').toLowerCase();
    var isLeopard = n.indexOf('leopard') > -1 || n.indexOf('gepard') > -1;
    // "prikk" is the Norwegian spelling of "prick"; without it "Prikkete sort"
    // renders as a flat dot instead of the print it actually is.
    var isDotted = n.indexOf('prick') > -1 || n.indexOf('prikk') > -1 || n.indexOf('pilkku') > -1 || n.indexOf('täplik') > -1 || n.indexOf('dot') > -1 || n.indexOf('flekk') > -1;
    if (isLeopard || isDotted) {
      // Ground colour comes from whatever colour word sits alongside "leopard";
      // the spots are always the dark tone the print actually uses.
      var ground = baseColour(n.replace('leopard', '').replace('gepard', '').replace('prickig', '').replace('prikkete', '').replace('prikket', '').replace('pilkullinen', '').replace('täplikäs', ''));
      var spot = 'rgba(40,28,20,.72)';
      if (n.indexOf('svart') > -1 || n.indexOf('black') > -1 || n.indexOf('sort') > -1 || n.indexOf('musta') > -1) {
        ground = '#2A2320'; spot = 'rgba(255,255,255,.55)';
      }
      var size = isDotted ? '7px 7px' : '9px 9px';
      return 'radial-gradient(' + spot + ' 34%, transparent 36%) 0 0/' + size +
             ', radial-gradient(' + spot + ' 30%, transparent 32%) 4px 4px/' + size +
             ', ' + ground;
    }
    return baseColour(n);
  }

  /* ==========================================================================
     1 · PREMIUM DROPDOWN
     Drawn on top of a real <select>, which stays in the DOM as the source of
     truth and keeps the form usable before this file runs.
     ========================================================================== */
  function buildSelect(sel, opts) {
    opts = opts || {};
    var isColor = sel.hasAttribute('data-color');
    var isSize = sel.hasAttribute('data-bra-size');
    var wrap = document.createElement('div');
    wrap.className = 'brasel' + (isColor ? ' brasel--color' : '') + (isSize ? ' brasel--size' : '');

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'brasel__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');

    // The <select> is about to be hidden, so the button has to carry its name.
    // A visible <label for> wins where one exists; otherwise the select's own
    // aria-label is copied across — which is what the bundle rows use, since
    // "BH #1" plus a colour swatch is clearer than a stack of field captions.
    var selId = sel.getAttribute('id');
    var labelEl = selId ? document.querySelector('label[for="' + selId + '"]') : null;
    if (labelEl) {
      if (!labelEl.id) labelEl.id = selId + '-label';
      btn.setAttribute('aria-labelledby', labelEl.id);
    } else if (sel.getAttribute('aria-label')) {
      btn.setAttribute('aria-label', sel.getAttribute('aria-label'));
    }

    var menu = document.createElement('div');
    menu.className = 'brasel__menu';
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;

    function chip(value) {
      if (!isColor) return '';
      var img = opts.images && opts.images[value];
      if (img) return '<i class="brasel__sw brasel__sw--img" style="background-image:url(' + img + ')"></i>';
      return '<i class="brasel__sw" style="background:' + swatchStyle(value) + '"></i>';
    }

    function close() {
      menu.hidden = true;
      wrap.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
    }
    function open() {
      menu.hidden = false;
      wrap.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    }

    function paint(value) {
      btn.innerHTML = '<span class="brasel__cur">' + chip(value) +
                      '<span>' + value + '</span></span><span class="brasel__caret"></span>';
      qsa('.brasel__opt', menu).forEach(function (o) {
        o.setAttribute('aria-selected', o.getAttribute('data-value') === value ? 'true' : 'false');
      });
    }

    function setValue(value, silent) {
      sel.value = value;
      paint(value);
      if (!silent) sel.dispatchEvent(new Event('change', { bubbles: true }));
    }

    Array.prototype.forEach.call(sel.options, function (opt) {
      var o = document.createElement('div');
      o.className = 'brasel__opt';
      o.setAttribute('role', 'option');
      o.setAttribute('data-value', opt.value);
      o.innerHTML = chip(opt.value) + '<span>' + opt.textContent + '</span>';
      o.addEventListener('click', function () { setValue(opt.value); close(); });
      menu.appendChild(o);
    });

    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (menu.hidden) open(); else close();
    });

    // Keyboard support. The native <select> is display:none once this runs, so
    // without this the picker would be unreachable without a mouse — and colour
    // and size are the only two decisions on the page that must be made.
    btn.addEventListener('keydown', function (e) {
      var options = qsa('.brasel__opt', menu);
      if (!options.length) return;
      var current = 0;
      options.forEach(function (o, i) { if (o.getAttribute('data-value') === sel.value) current = i; });

      switch (e.key) {
        case 'Escape':
          close();
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          if (menu.hidden) open();
          else setValue(options[Math.min(current + 1, options.length - 1)].getAttribute('data-value'));
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          if (menu.hidden) open();
          else setValue(options[Math.max(current - 1, 0)].getAttribute('data-value'));
          break;
        case 'Home':
          if (!menu.hidden) { e.preventDefault(); setValue(options[0].getAttribute('data-value')); }
          break;
        case 'End':
          if (!menu.hidden) { e.preventDefault(); setValue(options[options.length - 1].getAttribute('data-value')); }
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (menu.hidden) open(); else close();
          break;
      }
    });

    document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) close(); });

    wrap.appendChild(btn);
    wrap.appendChild(menu);
    sel.style.display = 'none';
    sel.parentNode.insertBefore(wrap, sel.nextSibling);
    paint(sel.value || (sel.options[0] && sel.options[0].value) || '');

    // Handed back so the bundle can repaint availability and set values itself.
    sel.__bra = {
      set: setValue,
      markUnavailable: function (unavailableValues) {
        qsa('.brasel__opt', menu).forEach(function (o) {
          var oos = unavailableValues.indexOf(o.getAttribute('data-value')) > -1;
          o.setAttribute('data-oos', oos ? 'true' : 'false');
        });
      }
    };
    return sel.__bra;
  }

  /* ==========================================================================
     2 · BUNDLE

     Size is one decision for the whole bundle; colour is per bra. The per-bra
     size <select>s still exist and are still what add-to-cart reads — they are
     just hidden and kept in sync with the global pill. Turning on "different
     sizes" only unhides them, so there is one code path for building the cart
     either way, not two.
     ========================================================================== */
  var bundle = qs('[data-bra-bundle]');
  var product = null;
  try { product = JSON.parse(qs('[data-bra-product-json]').textContent); } catch (e) { product = null; }

  if (bundle && product && product.variants) {
    (function () {
      var colorPos = parseInt(bundle.getAttribute('data-color-pos'), 10) || 0;
      var sizePos = parseInt(bundle.getAttribute('data-size-pos'), 10) || 0;
      var totalEl = qs('[data-bra-total]', bundle);
      var saveEl = qs('[data-bra-save]', bundle);
      var errEl = qs('[data-bra-err]', bundle);
      var addBtn = qs('[data-bra-add]', bundle);
      var saveTemplate = saveEl ? saveEl.getAttribute('data-template') : null;

      var sizePicker = qs('[data-bra-size-picker]');
      var sizePills = qsa('[data-bra-size-pill]');
      var sizeCurrentEl = qs('[data-bra-size-current]');
      var mixedToggle = qs('[data-bra-mixed-sizes]');
      var globalSize = sizePills.length ? sizePills[0].getAttribute('data-value') : null;

      /* ---- colour → variant photo, so picking a colour moves the gallery ---- */
      var colourImages = {};
      var colourSlide = {};
      if (colorPos) {
        product.variants.forEach(function (v) {
          var c = v.options[colorPos - 1];
          if (c && !colourImages[c] && v.featured_image && v.featured_image.src) {
            var s = v.featured_image.src;
            colourImages[c] = s + (s.indexOf('?') > -1 ? '&' : '?') + 'width=96&height=96&crop=center';
            colourSlide[c] = v.featured_image.position;
          }
        });
      }

      /* Move the gallery to the photo of the colour the shopper just picked.

         Every colour row drives this, not only the first bra: whichever swatch
         she just touched is the one she is trying to look at, so that is the
         photo to show. Picking the second bra's colour is exactly when she
         wants to see that second colour.

         On a phone the gallery sits a screen or more above the bundle, so
         changing the photo silently would change something she cannot see —
         hence the scroll. It only fires when the photo is actually off-screen,
         so the desktop side-by-side layout never jumps under her. */
      var galleryStage = qs('.brapdp__stage');
      var galleryThumbs = qs('.brapdp__thumbs');

      function showColour(colour) {
        var slide = colourSlide[colour];
        if (!slide || !window.LovelleShowSlide) return;
        // A clip may sit inside the gallery (data-video-slot = its slide index);
        // photos from that position on are one slide further along.
        var idx = slide - 1;
        var vslot = galleryStage ? parseInt(galleryStage.getAttribute('data-video-slot'), 10) : NaN;
        if (!isNaN(vslot) && idx >= vslot) idx += 1;
        window.LovelleShowSlide(idx);

        /* The strip collapses to four thumbs plus a "+N" button. A colour whose
           photo sits past that would leave the strip with nothing highlighted
           while the main image changed, which reads as broken — so open it. */
        if (galleryThumbs && slide > 4) galleryThumbs.classList.remove('is-collapsed');

        if (!galleryStage) return;
        var box = galleryStage.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var onScreen = box.bottom > 80 && box.top < vh - 80;
        if (onScreen) return;
        galleryStage.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      }

      function findVariant(colour, size) {
        for (var i = 0; i < product.variants.length; i++) {
          var v = product.variants[i];
          if (colorPos && v.options[colorPos - 1] !== colour) continue;
          if (sizePos && v.options[sizePos - 1] !== size) continue;
          return v;
        }
        return null;
      }

      function isMixed() { return !!(mixedToggle && mixedToggle.checked); }

      /* ---- Build the premium dropdowns ---- */
      qsa('[data-bra-pick]', bundle).forEach(function (pick) {
        qsa('select', pick).forEach(function (sel) { buildSelect(sel, { images: colourImages }); });
      });

      /* ---- Availability -------------------------------------------------
         Which way round this runs depends on the mode. With one size for the
         whole bundle, the size is fixed and the colours that do not come in it
         are struck through. With mixed sizes it goes back to per-row: the
         colour is fixed and the sizes are struck. Either way nothing is
         removed from the list — a picker that changes shape as you use it is
         disorienting, and "sold out in your size" is useful information. */
      function refreshPick(pick) {
        if (!colorPos || !sizePos) return;
        var colourSel = qs('[data-bra-color]', pick);
        var sizeSel = qs('[data-bra-size]', pick);
        if (!colourSel || !sizeSel) return;

        if (isMixed()) {
          if (sizeSel.__bra) {
            var deadSizes = [];
            Array.prototype.forEach.call(sizeSel.options, function (o) {
              var v = findVariant(colourSel.value, o.value);
              if (!v || v.available === false) deadSizes.push(o.value);
            });
            sizeSel.__bra.markUnavailable(deadSizes);
          }
          if (colourSel.__bra) colourSel.__bra.markUnavailable([]);
        } else {
          if (colourSel.__bra) {
            var deadColours = [];
            Array.prototype.forEach.call(colourSel.options, function (o) {
              var v = findVariant(o.value, globalSize);
              if (!v || v.available === false) deadColours.push(o.value);
            });
            colourSel.__bra.markUnavailable(deadColours);
          }
        }
      }
      function refreshAllPicks() { qsa('[data-bra-pick]', bundle).forEach(refreshPick); }

      /* ---- Global size --------------------------------------------------- */
      function applyGlobalSize(value, silent) {
        globalSize = value;
        sizePills.forEach(function (pill) {
          var on = pill.getAttribute('data-value') === value;
          pill.classList.toggle('is-active', on);
          pill.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        if (sizeCurrentEl) sizeCurrentEl.textContent = value;

        // Every hidden per-bra size follows the global one, so add-to-cart
        // always reads the same place whether or not mixed sizes are on.
        if (!isMixed()) {
          qsa('select[data-bra-size]', bundle).forEach(function (sel) {
            if (sel.__bra) sel.__bra.set(value, true); else sel.value = value;
          });
        }
        if (!silent) refreshAllPicks();
      }

      // A size that exists in no colour at all is marked, not hidden.
      if (sizePills.length && colorPos && sizePos) {
        sizePills.forEach(function (pill) {
          var value = pill.getAttribute('data-value');
          var any = product.variants.some(function (v) {
            return v.options[sizePos - 1] === value && v.available !== false;
          });
          if (!any) pill.setAttribute('data-oos', 'true');
        });
      }

      sizePills.forEach(function (pill) {
        pill.addEventListener('click', function () { applyGlobalSize(pill.getAttribute('data-value')); });
      });

      if (mixedToggle) {
        mixedToggle.addEventListener('change', function () {
          var mixed = mixedToggle.checked;
          if (sizePicker) sizePicker.classList.toggle('is-mixed', mixed);
          qsa('[data-bra-pick-size]', bundle).forEach(function (f) { f.hidden = !mixed; });
          // Coming back to one size resets every row to it, so the state on
          // screen and the state in the cart never diverge.
          if (!mixed) applyGlobalSize(globalSize);
          else refreshAllPicks();
        });
      }

      if (globalSize) applyGlobalSize(globalSize, true);
      refreshAllPicks();

      /* ---- Tier selection ---- */
      var qty = 1;

      function selectTier(input) {
        qty = parseInt(input.getAttribute('data-qty'), 10) || 1;
        qsa('[data-bra-tier-box]', bundle).forEach(function (box) {
          var mine = box.contains(input);
          box.classList.toggle('is-selected', mine);
          var picks = qs('[data-bra-picks]', box);
          if (picks) picks.hidden = !mine;
        });

        // Only the add-to-cart button carries the pack total. The sticky bar
        // deliberately keeps showing the price of one bra, so it is not touched
        // here.
        var total = input.getAttribute('data-total') || '';
        if (totalEl) totalEl.textContent = total;

        var save = input.getAttribute('data-save');
        if (saveEl) {
          saveEl.hidden = !(save && saveTemplate);
          if (save && saveTemplate) saveEl.textContent = saveTemplate.replace('%s', save);
        }
        if (errEl) errEl.hidden = true;
      }

      bundle.addEventListener('change', function (e) {
        if (!e.target || !e.target.matches) return;
        if (e.target.matches('input[data-bra-tier]')) { selectTier(e.target); return; }

        var pick = e.target.closest ? e.target.closest('[data-bra-pick]') : null;
        if (!pick) return;

        if (e.target.matches('select[data-bra-color]')) {
          refreshPick(pick);
          showColour(e.target.value);
        } else if (e.target.matches('select[data-bra-size]') && isMixed()) {
          refreshPick(pick);
        }
      });

      var checked = qs('input[data-bra-tier]:checked', bundle) || qs('input[data-bra-tier]', bundle);
      if (checked) { checked.checked = true; selectTier(checked); }

      /* ---- Add to cart ---- */
      if (addBtn) {
        addBtn.addEventListener('click', function () {
          var current = qs('input[data-bra-tier]:checked', bundle);
          var box = current ? current.closest('[data-bra-tier-box]') : null;
          var picks = box ? qsa('[data-bra-pick]', box) : [];
          var items = [];
          var missing = false;

          if (!picks.length) {
            var only = product.variants.filter(function (v) { return v.available !== false; })[0];
            if (only) items.push({ id: only.id, quantity: qty });
          } else {
            picks.forEach(function (pick) {
              var c = qs('[data-bra-color]', pick);
              var s = qs('[data-bra-size]', pick);
              var variant = findVariant(c ? c.value : null, s ? s.value : globalSize);
              if (variant && variant.available !== false) items.push({ id: variant.id, quantity: 1 });
              else missing = true;
            });
          }

          if (missing || !items.length) {
            if (errEl) {
              errEl.textContent = bundle.getAttribute('data-msg-unavailable') || '';
              errEl.hidden = false;
            }
            return;
          }
          if (errEl) errEl.hidden = true;

          // Same variant twice becomes one line of two, not two lines of one.
          var merged = {};
          items.forEach(function (it) { merged[it.id] = (merged[it.id] || 0) + it.quantity; });
          var payload = Object.keys(merged).map(function (id) {
            return { id: parseInt(id, 10), quantity: merged[id] };
          });

          if (typeof window.LovelleAddToCart === 'function') {
            window.LovelleAddToCart(payload, addBtn, { showUpsell: true });
          } else {
            var url = (window.Lovelle && window.Lovelle.routes && window.Lovelle.routes.cart_add_url) || '/cart/add.js';
            fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: payload })
            }).then(function () { window.location.href = '/cart'; })
              .catch(function () {
                if (errEl) { errEl.textContent = bundle.getAttribute('data-msg-error') || ''; errEl.hidden = false; }
              });
          }
        });
      }

      /* ---- Scroll-to-buy buttons elsewhere on the page ---- */
      qsa('[data-bra-scroll-to-buy]').forEach(function (b) {
        b.addEventListener('click', function () {
          var wanted = b.getAttribute('data-tier');
          if (wanted) {
            // Match the tier NUMBER, not its quantity. Matching on data-qty
            // only worked while tier 1 happened to hold two bras; on a 1/3/5
            // ladder no tier has a quantity of 2 and the button selected
            // nothing at all.
            var target = qs('input[data-bra-tier][data-tier-index="' + wanted + '"]', bundle);
            if (target && !target.checked) { target.checked = true; selectTier(target); }
          }
          bundle.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
        });
      });

      // The sticky bar takes her TO the bundle rather than buying for her. She
      // still has a colour, a size and a quantity to choose, and adding a
      // default set behind her back is how a cart gets abandoned at checkout.
      var stickyBuy = qs('[data-bra-sticky-buy]');
      if (stickyBuy) {
        stickyBuy.addEventListener('click', function () {
          bundle.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
        });
      }

      // The size guide writes its answer into the global pill, which cascades
      // to every bra in every tier. If she had switched to per-bra sizes, that
      // is switched back first — otherwise the guide would hand her an answer
      // that visibly changes nothing.
      window.LovelleBraSetSize = function (size) {
        var match = sizePills.filter(function (p) {
          return p.getAttribute('data-value').toLowerCase() === String(size).toLowerCase();
        })[0];
        if (match) {
          if (mixedToggle && mixedToggle.checked) {
            mixedToggle.checked = false;
            mixedToggle.dispatchEvent(new Event('change', { bubbles: true }));
          }
          applyGlobalSize(match.getAttribute('data-value'));
          return;
        }
        // No global pills (single-size product): fall back to the row selects.
        qsa('select[data-bra-size]', bundle).forEach(function (sel) {
          var opt = Array.prototype.filter.call(sel.options, function (o) {
            return o.value.toLowerCase() === String(size).toLowerCase();
          })[0];
          if (opt && sel.__bra) sel.__bra.set(opt.value);
        });
      };
    })();
  }

  /* ==========================================================================
     3 · SIZE GUIDE

     The mapping is one band per size — EU 70 = S, 75 = M, 80 = L, 85 = XL,
     90 = 2XL, 95 = 3XL — which is what the supplier's garment measurements and
     standard European band sizing both give. So the band picks the size, and the
     cup only moves her up when it is larger than that size actually carries
     (S and M stop at E; L and up stop at F).

     Anything E or larger additionally surfaces the supplier's own "size up for a
     fuller bust" advice as a swap she can take — offered, never applied behind
     her back. See sections/bra-size-guide.liquid for the data and the reasoning.
     ========================================================================== */
  if (sizeGuideEl) {
    (function () {
      var data = {};
      try { data = JSON.parse(sizeGuideEl.getAttribute('data-sizes')); } catch (e) { return; }

      var panel = qs('.brasg__panel', sizeGuideEl);
      var bandsWrap = qs('[data-bra-sg-bands]', sizeGuideEl);
      var cupsWrap = qs('[data-bra-sg-cups]', sizeGuideEl);
      var bandHelp = qs('[data-bra-sg-band-help]', sizeGuideEl);
      var resultEl = qs('[data-bra-sg-result]', sizeGuideEl);
      var basisEl = qs('[data-bra-sg-basis]', sizeGuideEl);
      var useLabel = qs('[data-bra-sg-use-label]', sizeGuideEl);
      var useBtn = qs('[data-bra-sg-use]', sizeGuideEl);
      var fullBustEl = qs('[data-bra-sg-fullbust]', sizeGuideEl);
      var swapBtn = qs('[data-bra-sg-swap]', sizeGuideEl);
      var measureErr = qs('[data-bra-sg-measure-error]', sizeGuideEl);
      var lastFocus = null;

      var state = { system: 'eu', band: -1, cup: -1, baseIndex: 0, shownIndex: 0, swapped: false };

      /* ---- open / close ---- */
      function open() {
        lastFocus = document.activeElement;
        sizeGuideEl.hidden = false;
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(function () { sizeGuideEl.classList.add('is-open'); });
        var first = qs('.brasg__close', sizeGuideEl);
        if (first) first.focus();
      }
      function close() {
        sizeGuideEl.classList.remove('is-open');
        document.body.style.overflow = '';
        setTimeout(function () { sizeGuideEl.hidden = true; }, 260);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }
      qsa('[data-sizeguide-open]').forEach(function (b) { b.addEventListener('click', open); });
      qsa('[data-bra-sg-close]', sizeGuideEl).forEach(function (b) { b.addEventListener('click', close); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !sizeGuideEl.hidden) close();
      });
      // Keep tabbing inside the dialog while it is open.
      if (panel) {
        panel.addEventListener('keydown', function (e) {
          if (e.key !== 'Tab') return;
          var focusable = qsa('button:not([disabled]), input, [tabindex]:not([tabindex="-1"])', panel)
            .filter(function (el) { return el.offsetParent !== null; });
          if (!focusable.length) return;
          var first = focusable[0], last = focusable[focusable.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        });
      }

      /* ---- tabs ---- */
      function showTab(name) {
        qsa('[data-bra-sg-tab]', sizeGuideEl).forEach(function (t) {
          var on = t.getAttribute('data-bra-sg-tab') === name;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        qsa('[data-bra-sg-pane]', sizeGuideEl).forEach(function (p) {
          p.classList.toggle('is-active', p.getAttribute('data-bra-sg-pane') === name);
        });
        var scroll = qs('.brasg__scroll', sizeGuideEl);
        if (scroll) scroll.scrollTop = 0;
      }
      qsa('[data-bra-sg-tab]', sizeGuideEl).forEach(function (tab) {
        tab.addEventListener('click', function () { showTab(tab.getAttribute('data-bra-sg-tab')); });
      });
      var gotoFinder = qs('[data-bra-sg-goto-finder]', sizeGuideEl);
      if (gotoFinder) gotoFinder.addEventListener('click', function () { showTab('finder'); });

      /* ---- steps ---- */
      var history = [];
      function showStep(name, remember) {
        if (remember !== false) {
          var current = qs('.brasg__step.is-active', sizeGuideEl);
          if (current) history.push(current.getAttribute('data-bra-sg-step'));
        }
        qsa('[data-bra-sg-step]', sizeGuideEl).forEach(function (s) {
          s.classList.toggle('is-active', s.getAttribute('data-bra-sg-step') === name);
        });
        var scroll = qs('.brasg__scroll', sizeGuideEl);
        if (scroll) scroll.scrollTop = 0;
      }
      qsa('[data-bra-sg-back]', sizeGuideEl).forEach(function (b) {
        b.addEventListener('click', function () { showStep(history.pop() || '1', false); });
      });
      var restart = qs('[data-bra-sg-restart]', sizeGuideEl);
      if (restart) restart.addEventListener('click', function () {
        state = { system: 'eu', band: -1, cup: -1, baseIndex: 0, shownIndex: 0, swapped: false };
        history = [];
        showStep('1', false);
      });

      /* ---- step 1: which system ---- */
      qsa('[data-bra-sg-system]', sizeGuideEl).forEach(function (b) {
        b.addEventListener('click', function () {
          state.system = b.getAttribute('data-bra-sg-system');
          qsa('[data-bra-sg-system]', sizeGuideEl).forEach(function (o) { o.classList.remove('is-active'); });
          b.classList.add('is-active');

          if (state.system === 'measure') { showStep('measure'); return; }

          // "75 in 75B" only makes sense in EU/FR; a UK or US shopper reads
          // "34 in 34B", so the helper sentence swaps with the system.
          if (bandHelp) {
            var alt = bandHelp.getAttribute('data-uk');
            var def = bandHelp.getAttribute('data-default');
            var useAlt = (state.system === 'uk' || state.system === 'us') && alt;
            bandHelp.textContent = useAlt ? alt : (def || bandHelp.textContent);
          }

          renderChoices(bandsWrap, data.bands[state.system], function (bandIndex) {
            state.band = bandIndex;
            renderChoices(cupsWrap, data.cups[state.system], function (cupIndex) {
              state.cup = cupIndex;
              var read = data.bands[state.system][bandIndex] +
                         data.cups[state.system][cupIndex] +
                         ' (' + state.system.toUpperCase() + ')';
              var tpl = basisEl ? basisEl.getAttribute('data-template') : null;
              finish(tpl ? tpl.replace('%s', read) : read);
            });
            showStep('3');
          });
          showStep('2');
        });
      });

      function renderChoices(wrap, values, onPick) {
        if (!wrap) return;
        wrap.innerHTML = '';
        values.forEach(function (value, index) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'brasg__opt';
          b.textContent = value;
          b.addEventListener('click', function () {
            qsa('.brasg__opt', wrap).forEach(function (o) { o.classList.remove('is-active'); });
            b.classList.add('is-active');
            onPick(index);
          });
          wrap.appendChild(b);
        });
      }

      /* ---- measure path ---- */
      var goBtn = qs('[data-bra-sg-measure-go]', sizeGuideEl);
      if (goBtn) goBtn.addEventListener('click', function () {
        var bandInput = qs('[data-bra-sg-mband]', sizeGuideEl);
        var bustInput = qs('[data-bra-sg-mbust]', sizeGuideEl);
        var band = parseFloat(bandInput ? bandInput.value : '');
        var bust = parseFloat(bustInput ? bustInput.value : '');

        // Bust smaller than underbust means the tape was read the wrong way
        // round. Say so rather than returning a confident wrong size.
        if (!band || !bust || bust <= band) {
          if (measureErr) measureErr.hidden = false;
          return;
        }
        if (measureErr) measureErr.hidden = true;

        var bandIndex = -1;
        data.bandRanges.forEach(function (range, i) {
          if (band >= range[0] && band <= range[1]) bandIndex = i;
        });
        if (bandIndex === -1) bandIndex = band < data.bandRanges[0][0] ? 0 : data.bandRanges.length - 1;

        var diff = bust - band;
        var cupIndex = 0;
        for (var i = 0; i < data.cupSteps.length; i++) { if (diff >= data.cupSteps[i]) cupIndex = i + 1; }

        state.band = bandIndex;
        state.cup = Math.min(cupIndex, data.cups.eu.length - 1);

        var measureTpl = basisEl ? basisEl.getAttribute('data-measure-template') : null;
        finish(measureTpl ? measureTpl.replace('%1', band).replace('%2', bust)
                          : data.bands.eu[bandIndex] + data.cups.eu[state.cup] + ' (EU)');
      });

      /* ---- the mapping itself ---- */
      function resolveIndex(bandIndex, cupIndex) {
        /* Two chart shapes, because bra sizing does not come in one.

           A full [cup][band] grid wins whenever the template supplies one.
           Charts like S–XXL across bands 70–100 and cups A–F genuinely depend
           on both axes — band 90 is L in an A cup but XXL in an E — and the
           band-then-bump model below cannot express that.

           A cell of -1 means we do not make that combination. Rather than
           quietly handing back a size that will not fit, fall back to the
           largest size the cup row does carry; the table shows the gap as "–"
           so she can see it for herself. */
        if (data.grid && data.grid.length) {
          var row = data.grid[Math.min(cupIndex, data.grid.length - 1)];
          if (row && row.length) {
            var cell = row[Math.min(bandIndex, row.length - 1)];
            if (cell !== undefined && cell !== null && cell >= 0) return cell;
            for (var k = row.length - 1; k >= 0; k--) {
              if (row[k] !== undefined && row[k] !== null && row[k] >= 0) return row[k];
            }
          }
          return data.sizes.length - 1;
        }

        var si = data.bandToSize[bandIndex];
        if (si === undefined || si === null) si = data.bandToSize[data.bandToSize.length - 1];
        // A cup the band does not carry moves her up one size rather than being
        // quietly accepted — that mismatch is the biggest single return driver.
        if (cupIndex > data.maxCup[si]) si = Math.min(si + 1, data.sizes.length - 1);
        return si;
      }

      function paintSize(index) {
        state.shownIndex = index;
        var size = data.sizes[index];
        if (resultEl) resultEl.textContent = size;
        if (useLabel) {
          var tpl = useLabel.getAttribute('data-template');
          useLabel.textContent = tpl ? tpl.replace('%s', size) : size;
        }
        // Highlight the row in both tables, so switching tabs confirms the
        // answer instead of restating it.
        qsa('.brasg__table tbody tr', sizeGuideEl).forEach(function (tr) {
          tr.classList.toggle('is-match', tr.getAttribute('data-size') === size);
        });
        // The grid keeps the size on each cell, not the row: light every
        // cell that says her size, whichever band/cup layout the grid uses.
        qsa('.brasg__table--grid td[data-size]', sizeGuideEl).forEach(function (td) {
          td.classList.toggle('is-match', td.getAttribute('data-size') === size);
        });
        if (swapBtn) {
          var up = Math.min(state.baseIndex + 1, data.sizes.length - 1);
          var swapped = index !== state.baseIndex;
          var t = swapBtn.getAttribute(swapped ? 'data-back-template' : 'data-up-template') || '%s';
          swapBtn.textContent = t.replace('%s', data.sizes[swapped ? state.baseIndex : up]);
        }
      }

      // basisSentence arrives already complete — each path fills its own
      // template, because "Baserat på 75B (EU)" and "Baserat på 75 cm underbyst
      // och 90 cm byst" are two different sentences, not one with a swappable
      // middle.
      function finish(basisSentence) {
        state.baseIndex = resolveIndex(state.band, state.cup);
        state.swapped = false;
        if (basisEl) basisEl.textContent = basisSentence;
        paintSize(state.baseIndex);

        // Nothing to offer if she is already in the largest size we make — and
        // nothing to offer at all on a grid chart, where the cup is already
        // baked into the answer instead of being a nudge on top of it.
        var fullBust = data.fullBustCup != null && !data.grid &&
                       state.cup >= data.fullBustCup &&
                       state.baseIndex < data.sizes.length - 1;
        if (fullBustEl) fullBustEl.hidden = !fullBust;

        showStep('result');
      }

      if (swapBtn) swapBtn.addEventListener('click', function () {
        state.swapped = !state.swapped;
        paintSize(state.swapped ? Math.min(state.baseIndex + 1, data.sizes.length - 1) : state.baseIndex);
      });

      if (useBtn) useBtn.addEventListener('click', function () {
        var size = data.sizes[state.shownIndex];
        if (size && typeof window.LovelleBraSetSize === 'function') window.LovelleBraSetSize(size);
        close();
        var target = qs('[data-bra-bundle]');
        if (target) target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      });
    })();
  }


  /* ==========================================================================
     4 · VIDEO WALL
     Nothing is fetched until the section is nearly on screen, and once loaded
     only the cards actually visible are decoding.
     ========================================================================== */
  if (videoWall) {
    (function () {
      var track = qs('[data-bra-video-track]', videoWall);
      var cards = qsa('.bravid__card', videoWall);
      var videos = qsa('[data-bra-video]', videoWall);
      var dots = qsa('[data-bra-video-dots] span', videoWall);
      var prev = qs('[data-bra-video-prev]', videoWall);
      var next = qs('[data-bra-video-next]', videoWall);
      if (!track || !cards.length) return;

      var interval = parseInt(videoWall.getAttribute('data-interval'), 10) || 3800;
      var saveData = !!(navigator.connection && navigator.connection.saveData);
      var loaded = false;
      var timer = null;
      var idleUntil = 0;

      function loadSources() {
        if (loaded) return;
        loaded = true;
        videos.forEach(function (v) {
          var src = v.getAttribute('data-src');
          if (!src || v.getAttribute('src')) return;
          v.setAttribute('src', src);
          v.load();
        });
      }

      /* ---- Play only what is on screen ---- */
      function watchCards() {
        if (!('IntersectionObserver' in window)) {
          videos.forEach(function (v) { v.play().catch(function () {}); });
          return;
        }
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var v = qs('[data-bra-video]', entry.target);
            if (!v) return;
            if (entry.isIntersecting) { v.play().catch(function () {}); }
            else if (!v.paused) { v.pause(); }
          });
        }, { threshold: 0.35 });
        cards.forEach(function (c) { io.observe(c); });
      }

      /* ---- Auto-advance ---- */
      function step() {
        var card = cards[0];
        if (!card) return;
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 10;
        var width = card.getBoundingClientRect().width + gap;
        var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        track.scrollTo({
          left: atEnd ? 0 : track.scrollLeft + width,
          behavior: reduced ? 'auto' : 'smooth'
        });
      }
      function start() {
        if (reduced || timer) return;
        timer = setInterval(function () {
          if (Date.now() < idleUntil) return;      // a human just touched it
          if (document.hidden) return;             // background tab
          step();
        }, interval);
      }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }
      function pauseBriefly() { idleUntil = Date.now() + 7000; }

      ['pointerdown', 'touchstart', 'wheel'].forEach(function (ev) {
        track.addEventListener(ev, pauseBriefly, { passive: true });
      });
      track.addEventListener('mouseenter', function () { idleUntil = Infinity; });
      track.addEventListener('mouseleave', function () { idleUntil = Date.now() + 2000; });

      if (prev) prev.addEventListener('click', function () {
        pauseBriefly();
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 10;
        track.scrollBy({ left: -(cards[0].getBoundingClientRect().width + gap), behavior: reduced ? 'auto' : 'smooth' });
      });
      if (next) next.addEventListener('click', function () { pauseBriefly(); step(); });

      /* ---- Dots follow the scroll position ---- */
      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking || !dots.length) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 10;
          var width = cards[0].getBoundingClientRect().width + gap;
          var index = Math.min(dots.length - 1, Math.max(0, Math.round(track.scrollLeft / width)));
          dots.forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
        });
      }, { passive: true });

      /* ---- Arm everything only once the section is close ---- */
      if ('IntersectionObserver' in window) {
        var armed = new IntersectionObserver(function (entries, obs) {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          if (!saveData) { loadSources(); watchCards(); }
          start();
        }, { rootMargin: '300px 0px' });
        armed.observe(videoWall);
      } else {
        loadSources();
        watchCards();
        start();
      }

      // On a metered connection the clips wait for a deliberate tap instead.
      if (saveData) {
        [prev, next].forEach(function (b) {
          if (b) b.addEventListener('click', function () { loadSources(); watchCards(); }, { once: true });
        });
      }

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) { videos.forEach(function (v) { if (!v.paused) v.pause(); }); stop(); }
        else if (loaded) { start(); }
      });
    })();
  }

  /* ==========================================================================
     5 · REVIEW SUMMARY — bars sweep, average counts up

     Runs for every summary block on the page: the one in the buy-box accordion
     and the one in the review section. Each plays once.

     The trigger differs by where it sits. The section block waits for an
     IntersectionObserver, because the animation is only worth anything if she is
     looking at it. The accordion block is display:none until opened, so an
     observer would fire on a zero-height element and the sweep would be over
     before she saw it — that one is kicked by the accordion's own click.
     ========================================================================== */
  function playSummary(block) {
    if (!block || block.hasAttribute('data-played')) return;
    block.setAttribute('data-played', '');

    qsa('[data-bra-bar]', block).forEach(function (bar, i) {
      // Custom properties come back with the author's whitespace intact.
      var target = (bar.style.getPropertyValue('--w') || '0%').trim();
      if (reduced) { bar.style.width = target; return; }
      bar.style.width = '0%';
      // Staggered from 5 stars down, so the eye follows the tallest bar first.
      setTimeout(function () { bar.style.width = target; }, 90 + i * 70);
    });

    var avg = qs('[data-bra-count-to]', block);
    if (!avg) return;
    var raw = avg.getAttribute('data-bra-count-to') || '';
    // "4,8" in Swedish, "4.8" elsewhere — count in numbers, print in her format.
    var decimalComma = raw.indexOf(',') > -1;
    var target = parseFloat(raw.replace(',', '.'));
    if (isNaN(target)) return;
    if (reduced) { avg.textContent = raw; return; }

    var start = null;
    var duration = 900;
    function frame(now) {
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / duration);
      // ease-out: fast at first, settles onto the number
      var eased = 1 - Math.pow(1 - t, 3);
      var value = (target * eased).toFixed(1);
      avg.textContent = decimalComma ? value.replace('.', ',') : value;
      if (t < 1) requestAnimationFrame(frame);
      else avg.textContent = raw;
    }
    avg.textContent = decimalComma ? '0,0' : '0.0';
    requestAnimationFrame(frame);
  }

  (function () {
    var blocks = qsa('[data-bra-summary]');
    if (!blocks.length) return;

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          playSummary(entry.target);
        });
      }, { threshold: 0.4 });
      blocks.forEach(function (b) {
        // Inside a closed accordion this would fire against a zero-height box.
        if (b.closest('.accordion__body')) return;
        io.observe(b);
      });
    } else {
      blocks.forEach(playSummary);
    }

    // Accordion blocks play on open. theme.js owns the accordion itself, so this
    // listens rather than reimplementing it — and reads the result on the next
    // tick rather than assuming whose click handler ran first. bra.js is loaded
    // from <head> and theme.js from the end of <body>, so this one actually
    // fires FIRST and the panel is still closed at that moment.
    qsa('.accordion__trigger').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var body = btn.nextElementSibling;
        if (!body) return;
        setTimeout(function () {
          if (body.hidden) return;
          qsa('[data-bra-summary]', body).forEach(playSummary);
        }, 0);
      });
    });
  })();

  /* ==========================================================================
     6 · REVIEWS — filters, fold, lightbox
     ========================================================================== */
  if (reviewSection) {
    (function () {
      var list = qs('[data-bra-review-wrap] .bralist', reviewSection);
      var moreBtn = qs('[data-bra-review-more]', reviewSection);
      var moreWrap = qs('.brarevs__more', reviewSection);
      if (!list) return;

      var cards = qsa('.brarev', list);
      // Liquid already added .is-folded. Only take it off when there was never
      // anything to fold.
      var folded = cards.length > 6;
      if (!folded) {
        list.classList.remove('is-folded');
        if (moreWrap) moreWrap.hidden = true;
      }

      function applyFilter(kind) {
        cards.forEach(function (card) {
          var keep = true;
          if (kind === 'photo') keep = card.getAttribute('data-photo') === 'true';
          else if (kind === '5') keep = card.getAttribute('data-rating') === '5';
          card.hidden = !keep;
        });
        // A filtered view is short enough to show whole; folding it would hide
        // matches the shopper explicitly asked for.
        var visible = cards.filter(function (c) { return !c.hidden; }).length;
        var needsFold = kind === 'all' && folded && visible > 6;
        list.classList.toggle('is-folded', needsFold);
        if (moreWrap) moreWrap.hidden = !needsFold;
      }

      qsa('[data-bra-filter]', reviewSection).forEach(function (chip) {
        chip.addEventListener('click', function () {
          qsa('[data-bra-filter]', reviewSection).forEach(function (c) { c.classList.remove('is-active'); });
          chip.classList.add('is-active');
          applyFilter(chip.getAttribute('data-bra-filter'));
        });
      });

      if (moreBtn) moreBtn.addEventListener('click', function () {
        folded = false;
        list.classList.remove('is-folded');
        if (moreWrap) moreWrap.hidden = true;
      });
    })();
  }

  /* ==========================================================================
     7 · PHOTO LIGHTBOX
     Bound document-wide so it covers the photos in the buy-box accordion as
     well as the ones in the review section.
     ========================================================================== */
  (function () {
    var box = qs('[data-bra-lightbox-modal]');
    var boxImg = box ? qs('[data-bra-lightbox-img]', box) : null;
    if (!box || !boxImg) return;
    var lastFocus = null;

    function closeBox() {
      box.classList.remove('is-open');
      setTimeout(function () { box.hidden = true; boxImg.removeAttribute('src'); }, 220);
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest ? e.target.closest('[data-bra-lightbox]') : null;
      if (!trigger) return;
      lastFocus = trigger;
      boxImg.src = trigger.getAttribute('data-full');
      box.hidden = false;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(function () { box.classList.add('is-open'); });
      var closeBtn = qs('[data-bra-lightbox-close]', box);
      if (closeBtn) closeBtn.focus();
    });

    var closeBtn = qs('[data-bra-lightbox-close]', box);
    if (closeBtn) closeBtn.addEventListener('click', closeBox);
    box.addEventListener('click', function (e) { if (e.target === box) closeBox(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !box.hidden) closeBox();
    });
  })();

  /* ==========================================================================
     3b · WAIST SIZE GUIDE
     One number in, one size out — for garments sized on the waist (the 360°
     brief). Same panel, tabs and result card as the bra guide so the two
     products feel like one store, but its own small state machine: the bra
     guide's band/cup flow has nothing to say about a waistband.

     Ranges may overlap at the top of the chart (2XL 88–100, 3XL 92–112). The
     FIRST row containing the measurement wins, i.e. the smaller size —
     compression only works if it is felt, and a size too big is just underwear.
     ========================================================================== */
  var waistGuideEl = document.querySelector('[data-bra-waistguide]');
  if (waistGuideEl) {
    (function () {
      var rows = [];
      try { rows = JSON.parse(waistGuideEl.getAttribute('data-rows')) || []; } catch (e) { return; }
      if (!rows.length) return;

      var panel = qs('.brasg__panel', waistGuideEl);
      var input = qs('[data-bra-wg-input]', waistGuideEl);
      var errEl = qs('[data-bra-wg-error]', waistGuideEl);
      var resultEl = qs('[data-bra-wg-result]', waistGuideEl);
      var basisEl = qs('[data-bra-wg-basis]', waistGuideEl);
      var useBtn = qs('[data-bra-wg-use]', waistGuideEl);
      var useLabel = qs('[data-bra-wg-use-label]', waistGuideEl);
      var goBtn = qs('[data-bra-wg-go]', waistGuideEl);
      var restartBtn = qs('[data-bra-wg-restart]', waistGuideEl);
      var current = null;
      var lastFocus = null;

      /* ---- open / close ---- */
      function open() {
        lastFocus = document.activeElement;
        waistGuideEl.hidden = false;
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(function () { waistGuideEl.classList.add('is-open'); });
        // Land on the field, not the close button: the whole guide is one
        // number, so the keyboard should be up before she has to think.
        setTimeout(function () {
          if (!waistGuideEl.hidden && input && qs('[data-bra-sg-step="input"].is-active', waistGuideEl)) input.focus();
        }, 320);
      }
      function close() {
        waistGuideEl.classList.remove('is-open');
        document.body.style.overflow = '';
        setTimeout(function () { waistGuideEl.hidden = true; }, 260);
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }
      qsa('[data-sizeguide-open]').forEach(function (b) { b.addEventListener('click', open); });
      qsa('[data-bra-sg-close]', waistGuideEl).forEach(function (b) { b.addEventListener('click', close); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !waistGuideEl.hidden) close();
      });
      if (panel) {
        panel.addEventListener('keydown', function (e) {
          if (e.key !== 'Tab') return;
          var focusable = qsa('button:not([disabled]), input, [tabindex]:not([tabindex="-1"])', panel)
            .filter(function (el) { return el.offsetParent !== null; });
          if (!focusable.length) return;
          var first = focusable[0], last = focusable[focusable.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        });
      }

      /* ---- tabs ---- */
      function showTab(name) {
        qsa('[data-bra-sg-tab]', waistGuideEl).forEach(function (t) {
          var on = t.getAttribute('data-bra-sg-tab') === name;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        qsa('[data-bra-sg-pane]', waistGuideEl).forEach(function (p) {
          p.classList.toggle('is-active', p.getAttribute('data-bra-sg-pane') === name);
        });
        var scroll = qs('.brasg__scroll', waistGuideEl);
        if (scroll) scroll.scrollTop = 0;
      }
      qsa('[data-bra-sg-tab]', waistGuideEl).forEach(function (tab) {
        tab.addEventListener('click', function () { showTab(tab.getAttribute('data-bra-sg-tab')); });
      });
      qsa('[data-bra-sg-goto-finder]', waistGuideEl).forEach(function (b) {
        b.addEventListener('click', function () { showTab('finder'); if (input) input.focus(); });
      });

      /* ---- steps ---- */
      function showStep(name) {
        qsa('[data-bra-sg-step]', waistGuideEl).forEach(function (s) {
          s.classList.toggle('is-active', s.getAttribute('data-bra-sg-step') === name);
        });
        var scroll = qs('.brasg__scroll', waistGuideEl);
        if (scroll) scroll.scrollTop = 0;
      }

      /* ---- the mapping ---- */
      function resolve(cm) {
        for (var i = 0; i < rows.length; i++) {
          if (cm >= rows[i][1] && cm <= rows[i][2]) return rows[i][0];
        }
        return cm < rows[0][1] ? rows[0][0] : rows[rows.length - 1][0];
      }

      function paint(size, cm) {
        current = size;
        if (resultEl) resultEl.textContent = size;
        if (basisEl) basisEl.textContent = (basisEl.getAttribute('data-template') || '%s cm').replace('%s', cm);
        if (useLabel) useLabel.textContent = (useLabel.getAttribute('data-template') || '%s').replace('%s', size);
        // Highlight the row in the table, so switching tabs confirms the
        // answer instead of restating it.
        qsa('.brasg__table tbody tr', waistGuideEl).forEach(function (tr) {
          tr.classList.toggle('is-match', tr.getAttribute('data-size') === size);
        });
      }

      function go() {
        var cm = parseFloat(String(input ? input.value : '').replace(',', '.'));
        if (!cm || cm < 50 || cm > 130) {
          if (errEl) errEl.hidden = false;
          if (input) input.focus();
          return;
        }
        if (errEl) errEl.hidden = true;
        paint(resolve(cm), Math.round(cm));
        showStep('result');
      }
      if (goBtn) goBtn.addEventListener('click', go);
      if (input) {
        input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); go(); } });
        input.addEventListener('input', function () { if (errEl) errEl.hidden = true; });
      }
      if (restartBtn) restartBtn.addEventListener('click', function () {
        if (input) input.value = '';
        showStep('input');
        if (input) input.focus();
      });

      // Writes the answer into the global size pill, same hook the bra guide
      // uses, then takes her straight to the bundle.
      if (useBtn) useBtn.addEventListener('click', function () {
        if (current && typeof window.LovelleBraSetSize === 'function') window.LovelleBraSetSize(current);
        close();
        var target = qs('[data-bra-bundle]');
        if (target) target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      });
    })();
  }

  /* ==========================================================================
     5 · BEFORE / AFTER CLIP
     Fetched only when nearly on screen, playing only while on screen — the
     same rules as the video wall. A tap pauses and resumes, which is also how
     a reduced-motion visitor starts it, since for them it never autoplays.
     ========================================================================== */
  /* ==========================================================================
     5b · HOW-TO CLIP (sections/bra-howto-video.liquid)
     Identical rules to the before/after clip: fetched near the viewport,
     playing only while on screen, a tap pauses and resumes, and a
     reduced-motion visitor gets a play button instead of autoplay.
     ========================================================================== */
  var howEl = document.querySelector('[data-bra-howto]');
  if (howEl) {
    (function () {
      var video = qs('[data-bra-howto-video]', howEl);
      var toggle = qs('[data-bra-howto-toggle]', howEl);
      if (!video) return;
      var loaded = false;
      function load() {
        if (loaded) return;
        loaded = true;
        var src = video.getAttribute('data-src');
        if (src && !video.getAttribute('src')) { video.setAttribute('src', src); video.load(); }
      }
      function play() { load(); var p = video.play(); if (p && p.catch) p.catch(function () {}); }
      if (reduced) {
        if (toggle) toggle.hidden = false;
      } else if ('IntersectionObserver' in window) {
        var near = new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (e) { if (e.isIntersecting) { load(); obs.disconnect(); } });
        }, { rootMargin: '300px 0px' });
        near.observe(howEl);
        var vis = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) play();
            else if (!video.paused) video.pause();
          });
        }, { threshold: 0.4 });
        vis.observe(video);
      } else {
        play();
      }
      var stage = video.parentNode;
      if (stage) stage.addEventListener('click', function () {
        if (video.paused) play(); else video.pause();
        if (toggle) toggle.hidden = !video.paused;
      });
    })();
  }

  var baEl = document.querySelector('[data-bra-ba]');
  if (baEl) {
    (function () {
      var video = qs('[data-bra-ba-video]', baEl);
      var toggle = qs('[data-bra-ba-toggle]', baEl);
      if (!video) return;
      var loaded = false;

      function load() {
        if (loaded) return;
        loaded = true;
        var src = video.getAttribute('data-src');
        if (src && !video.getAttribute('src')) { video.setAttribute('src', src); video.load(); }
      }
      function play() { load(); video.play().catch(function () {}); }

      if (reduced) {
        if (toggle) toggle.hidden = false;
      } else if ('IntersectionObserver' in window) {
        var near = new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (e) { if (e.isIntersecting) { load(); obs.disconnect(); } });
        }, { rootMargin: '300px 0px' });
        near.observe(baEl);
        var vis = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) play();
            else if (!video.paused) video.pause();
          });
        }, { threshold: 0.4 });
        vis.observe(video);
      } else {
        play();
      }

      var stage = video.parentNode;
      if (stage) stage.addEventListener('click', function () {
        if (video.paused) play(); else video.pause();
        if (toggle) toggle.hidden = !video.paused;
      });
    })();
  }

  /* ==========================================================================
     8 · HAPPY CUSTOMERS — the review pill rotates (snippets/bra-happy.liquid)
     The photo strip is a CSS marquee and needs nothing here. The pills are a
     scroll-snap row: this only auto-advances it every few seconds, keeps the
     dots in step, and stays out of the way for seven seconds after a touch.
     ========================================================================== */
  qsa('[data-bra-happy]').forEach(function (strip) {
    var track = qs('[data-bra-happy-track]', strip);
    var dots = qsa('[data-bra-happy-dots] span', strip);
    if (!track) return;
    var pills = qsa('.brahappy__pill', track);
    if (pills.length < 2) return;
    var idleUntil = 0;
    var timer = null;

    function index() {
      var w = pills[0].getBoundingClientRect().width + 10;
      return Math.min(pills.length - 1, Math.max(0, Math.round(track.scrollLeft / w)));
    }
    function paintDots() {
      var i = index();
      dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
    }
    function step() {
      if (Date.now() < idleUntil || document.hidden) return;
      var w = pills[0].getBoundingClientRect().width + 10;
      var next = (index() + 1) % pills.length;
      track.scrollTo({ left: next * w, behavior: reduced ? 'auto' : 'smooth' });
    }
    ['pointerdown', 'touchstart', 'wheel'].forEach(function (ev) {
      track.addEventListener(ev, function () { idleUntil = Date.now() + 7000; }, { passive: true });
    });
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; paintDots(); });
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) { if (!timer) timer = setInterval(step, 4800); }
        else if (timer) { clearInterval(timer); timer = null; }
      }, { threshold: 0.3 }).observe(strip);
    } else {
      timer = setInterval(step, 4800);
    }
  });
})();
